import React, { FormEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputField, ModalView } from '@/modules/base';
import { useTransmitData } from '@/service';
import { Email } from '@/type';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import { NotificationBanner } from '@/modules/components';

type ContactComponentProps = {
  email: string;
  telephone: string;
  emailDisable: boolean;
};

export const ContactComponent = ({
  email,
  telephone,
  emailDisable,
}: ContactComponentProps) => {
  const intl = useIntl();
  const [fields, setFields] = useState<Email>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: boolean | undefined }>({
    initial: true,
  });
  const [isSending, setIsSending] = useState(false);

  const { mutateAsync, isError } = useTransmitData();

  const handleValidation = () => {
    const errors: { [key: string]: boolean } = {};
    //Name
    if (!fields['name']) {
      errors.name = true;
    }

    //Email
    if (!fields['email']) {
      errors.email = true;
    } else {
      const lastAtPos = fields['email'].lastIndexOf('@');
      const lastDotPos = fields['email'].lastIndexOf('.');

      if (
        !(
          lastAtPos < lastDotPos &&
          lastAtPos > 0 &&
          fields['email'].indexOf('@@') === -1 &&
          lastDotPos > 2 &&
          fields['email'].length - lastDotPos > 2
        )
      ) {
        errors.email = true;
      }
    }

    //Message
    if (!fields['message']) {
      errors.message = true;
    }
    return errors;
  };

  const handleFieldValueChange = (field: keyof Email, value: string) => {
    const checkFields: Email = { ...fields };
    checkFields[field] = value;
    setFields({ ...checkFields });
    setErrors({ ...handleValidation() });
  };

  const submitEmail = async (event: FormEvent) => {
    event.preventDefault();
    const email: Email = fields;
    setIsSending(true);
    await mutateAsync({
      path: '/api/email/contact',
      payload: { ...email },
    });
    setIsSending(false);
  };

  if (isError) {
    toast(
      <NotificationBanner
        description={'Sending Email failed! Please try again later.'}
        buttonText={'Close'}
        buttonAction={() => toast.dismiss()}
      />,
      {
        position: 'top-center',
        type: 'error',
      }
    );
  }

  return (
    <section id="contact">
      <div className="social_connect">
        <div className="text-content container">
          <div className="col-md-6">
            <span className="social_heading">
              <FormattedMessage id="app.contact.follow" />
            </span>
            <ul className="social_icons">
              <li>
                <span className="icon-twitter color_animation" />
              </li>
              <li>
                <span className="icon-github color_animation" />
              </li>
              <li>
                <span className="icon-linkedin color_animation" />
              </li>
              <li>
                <span className="icon-mail color_animation" />
              </li>
            </ul>
          </div>
          <div className="col-md-4">
            <span className="social_heading">
              <FormattedMessage id="app.contact" />
            </span>
            <span className="social_info">
              <a className="color_animation" href={'mailto:' + email + ''}>
                {email}
              </a>
              <a className="color_animation" href={'tel:' + telephone + ''}>
                {telephone}
              </a>
            </span>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row">
          <div className="col-md-12">
            <div className="inner">
              <div className="contact-form contact">
                <form id="contact-us" onSubmit={submitEmail}>
                  <div className="col-md-6 ">
                    <InputField
                      item={{
                        id: 'app.contact.name',
                        type: 'text',
                        defaultMessage: 'Name',
                        field: 'name',
                        className: 'form',
                        error: errors.name,
                      }}
                      onChange={handleFieldValueChange}
                    />
                    <InputField
                      item={{
                        id: 'app.contact.email',
                        type: 'text',
                        defaultMessage: 'Email',
                        field: 'email',
                        className: 'form',
                        error: errors.email,
                      }}
                      onChange={handleFieldValueChange}
                    />
                    <InputField
                      item={{
                        id: 'app.contact.subject',
                        type: 'text',
                        defaultMessage: 'Subject',
                        field: 'subject',
                        className: 'form',
                      }}
                      onChange={handleFieldValueChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <textarea
                      name="message"
                      className="form textarea"
                      onChange={(event) =>
                        handleFieldValueChange('message', event.target.value)
                      }
                      value={fields['message']}
                      placeholder={intl.formatMessage({
                        id: 'app.contact.message',
                        defaultMessage: 'Thema',
                      })}
                      style={{
                        borderColor: errors.message ? 'red' : '',
                      }}
                      rows={9}
                    />
                  </div>
                  <div className="relative fullwidth col-xs-12">
                    <button
                      type="submit"
                      style={{
                        color:
                          !isEmpty(errors) || emailDisable ? '#808080' : '',
                      }}
                      disabled={!isEmpty(errors) || emailDisable}
                      data-toggle="modal"
                      data-target="#orderPopup"
                      className="form-btn"
                    >
                      <FormattedMessage id="app.contact.send" />
                    </button>
                  </div>
                  <div className="clear" />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalView
        id="orderPopup"
        title={
          <h3>
            <FormattedMessage id="app.contact.confirm.title" />
          </h3>
        }
        body={
          <React.Fragment>
            <p>
              <FormattedMessage id="app.contact.confirm.message" />
            </p>
            <div>
              <p>{<span>{fields.message}</span>}</p>
            </div>
          </React.Fragment>
        }
        onSave={() => {}}
        disable={isSending}
        saveButtonLabel={<FormattedMessage id="app.contact.closeButton" />}
      />
    </section>
  );
};
